import { StyledSelect } from '@monorepo/shared/componentsV2/styled/form';
import {
  MathUnit,
  NullableNumber,
  NullableString,
  NumericFieldResponse,
  SaveFieldValueRequest,
  SaveNumericValue,
  SingleSelectFieldResponse,
  SingleSelectValue,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { FormValues } from './types';
import {
  stringFieldValueToFormInitialValue,
  getUnexpectedTypeErrorAndLogToSentry,
  getValidateFunction,
} from './utils';

function getSingleSelectOptions(
  field: SingleSelectFieldResponse,
  savedValue?: NullableString,
) {
  const options = [...field.options];
  if (savedValue && !options.includes(savedValue)) {
    options.push(savedValue);
  }
  return options.map((opt) => ({
    label: opt,
    value: opt,
  }));
}

type EditSingleSelectFieldValueProps = {
  className?: string;
  field: SingleSelectFieldResponse;
  fieldValue?: SingleSelectValue;
  isRequired: boolean;
  label: string;
  name: string;
  subText?: NullableString;
  isReadOnly?: boolean;
  isLabelHidden?: boolean;
  isErrorTextHidden?: boolean;
  isCompact?: boolean;
  isSubtextHidden?: boolean;
  forceShowErrors?: boolean;
};

export function EditSingleSelectFieldValue({
  className,
  field,
  fieldValue,
  isRequired,
  label,
  name,
  subText,
  isReadOnly,
  isLabelHidden,
  isErrorTextHidden,
  isCompact,
  isSubtextHidden,
  forceShowErrors,
}: EditSingleSelectFieldValueProps) {
  // History versions are seeded via form initialValues rather than fieldValue
  const {
    meta: { initial },
  } = useField<string | undefined>(name, { subscription: { initial: true } });
  const savedValue = fieldValue?.value ?? initial;
  const options = useMemo(
    () => getSingleSelectOptions(field, savedValue),
    [field, savedValue],
  );
  return (
    <StyledSelect
      className={className}
      options={options}
      name={name}
      label={label}
      initialValue={stringFieldValueToFormInitialValue(fieldValue)}
      showBlankOption={!isRequired}
      validate={getValidateFunction(isRequired)}
      required={isRequired}
      subText={subText}
      isReadOnly={isReadOnly}
      isLabelHidden={isLabelHidden}
      isErrorTextHidden={isErrorTextHidden}
      isCompact={isCompact}
      isSubtextHidden={isSubtextHidden}
      forceShowErrors={forceShowErrors}
    />
  );
}

export function getNumericFieldValueFromForm(
  field: NumericFieldResponse,
  formValues: FormValues,
): SaveFieldValueRequest {
  const value = formValues[field.id] == null ? null : formValues[field.id];
  let parsedValue: NullableNumber;

  if (value === null) {
    parsedValue = value;
  } else if (typeof value === 'string') {
    parsedValue = parseFloat(value);
    if (Number.isNaN(parsedValue)) {
      throw getUnexpectedTypeErrorAndLogToSentry(field, value);
    }
  } else {
    throw getUnexpectedTypeErrorAndLogToSentry(field, value);
  }

  const givenUnits = formValues[`${field.id}_unit`];

  if (
    givenUnits != null &&
    !(typeof givenUnits === 'string' && MathUnit.isValid(givenUnits))
  ) {
    throw new Error(
      `Unexpected value provided for units of ${field.name} (${givenUnits}).`,
    );
  }

  const fieldValue: SaveNumericValue = {
    value: parsedValue,
    givenUnits: givenUnits || null,
  };
  return {
    fieldId: field.id,
    fieldValue,
  };
}
